import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  createTeamShareLink,
  getAppSettings,
  getTeamStreamKeyAudit,
  listTeamShareLinks,
  revokeTeamShareLink,
  rotateTeamStreamKey,
  setTeamVisibility,
} from "@/lib/api";
import { TkticSelect } from "@/components/ui/TkticSelect";
import type {
  CreatedShareLink,
  ShareLink,
  TeamSettings,
  TeamSettingValidationError,
} from "@/lib/types";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";
import { invalidateScope, publicAppSettingsKey, unscopedKey } from "@/lib/queryClient";
import { PageHeader } from "./components/PageHeader";
import {
  SettingChipGroup,
  SettingChipMulti,
  SettingToggle,
} from "./components/SettingControls";
import { diffTeamSettings, isTeamSettingsDirty } from "./settingsDiff";
import {
  useTeamSettings,
  useTeamSettingsMutation,
} from "./hooks/useTeamSettings";

/**
 * Coach > Settings — the 7th coach tab (M6b).
 *
 * Ports the legacy `renderCoachTeamSettings` (`js/coaching.js`).
 * Two sections:
 *   1. AI Governance — `ai.drafting_enabled` · `ai.tone` ·
 *      `ai.never_draft_for_visibilities` · `ai.allowed_draft_targets`
 *   2. Coaching Defaults — `notes.default_visibility` ·
 *      `summaries.default_visibility` · `goals.default_visibility`
 *
 * Controls are rendered FROM the server `schema` payload — a
 * backend schema change (new choice, new key) flows through
 * without a client edit. The one deliberate divergence:
 * `summaries.default_visibility` drops the `player` choice
 * (`SUMMARY_VISIBILITY_OMIT`), because a `player`-visibility
 * summary is invisible to viewers — same call the M6a
 * `MatchSummaryComposerModal` fix made. See the M6b inventory Q1.
 *
 * `can_edit` (from the GET payload) gates the whole surface:
 * `false` for plain coaches ⇒ every control disabled + a "Read
 * only" pill + "ask a team admin" copy. The PATCH is also
 * capability-gated server-side — the disabled UI is the
 * primary guard, the 403 a backstop.
 *
 * Local edit state is seeded from the server payload and reset
 * whenever a fresh payload arrives (initial load, post-save,
 * scope change).
 */

/** `summaries.default_visibility` choices to hide — see the file
 * docstring + M6b inventory Q1. */
const SUMMARY_VISIBILITY_OMIT = new Set(["player"]);

/** Human labels for every setting key in `TEAM_SETTING_SCHEMAS`. */
const SETTING_LABELS: Record<string, { label: string; description: string }> = {
  "ai.drafting_enabled": {
    label: "Enable AI drafting",
    description: "Allow draft generation for approved targets only.",
  },
  "ai.tone": {
    label: "Draft tone",
    description: "The voice generated drafts are written in.",
  },
  "ai.never_draft_for_visibilities": {
    label: "Never draft or include context for",
    description:
      "Notes at these visibilities are excluded from drafting entirely.",
  },
  "ai.allowed_draft_targets": {
    label: "Allowed draft targets",
    description: "The fields AI drafting is permitted to generate.",
  },
  "notes.default_visibility": {
    label: "New note visibility",
    description: "The visibility a new coaching note starts at.",
  },
  "summaries.default_visibility": {
    label: "Match recap visibility",
    description: "The visibility a new match recap starts at.",
  },
  "goals.default_visibility": {
    label: "Goal visibility",
    description: "The visibility a new player goal starts at.",
  },
};

/** Human labels for every choice value across the schema. */
const CHOICE_LABELS: Record<string, string> = {
  // ai.tone
  direct: "Direct",
  encouraging: "Encouraging",
  technical: "Technical",
  // visibilities
  private: "Private",
  team: "Team",
  player: "Player + family",
  unlisted: "Unlisted",
  coach: "Coach / admin only",
  // ai.allowed_draft_targets
  player_summary: "Player summary",
  what_happened: "What happened",
  why_it_matters: "Why it matters",
  what_to_do_next: "What to do next",
  clip_title: "Clip title",
  clip_description: "Clip description",
  goal_description: "Goal description",
  goal_success_criteria: "Goal success criteria",
  summary_team_positives: "Summary positives",
  summary_team_improvements: "Summary improvements",
  summary_training_focus: "Training focus",
};

const choiceLabel = (v: string) => CHOICE_LABELS[v] ?? v;

/** Order the two sections render their keys in. */
const AI_KEYS = [
  "ai.drafting_enabled",
  "ai.tone",
  "ai.never_draft_for_visibilities",
  "ai.allowed_draft_targets",
] as const;
const DEFAULTS_KEYS = [
  "notes.default_visibility",
  "summaries.default_visibility",
  "goals.default_visibility",
] as const;

export function SettingsPage() {
  const toast = useToast();
  const settingsQuery = useTeamSettings();
  const mutation = useTeamSettingsMutation();

  // Local edit buffer, seeded from the server payload. Re-seeded
  // whenever a fresh payload lands (load / save / scope change).
  const [draft, setDraft] = useState<TeamSettings>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    TeamSettingValidationError[]
  >([]);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const payload = settingsQuery.data;
  useEffect(() => {
    if (payload) setDraft({ ...payload.settings });
  }, [payload]);

  const canEdit = payload?.can_edit ?? false;

  // Dirty check — drives the Save button's enabled state.
  const isDirty = useMemo(
    () => (payload ? isTeamSettingsDirty(payload.settings, draft) : false),
    [draft, payload],
  );

  const setKey = (key: keyof TeamSettings, value: unknown) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    // A fresh edit clears the last save's status.
    setSavedAt(null);
    setSaveError(null);
    setValidationErrors([]);
  };

  const handleSave = async () => {
    if (!payload || !canEdit) return;
    setSaveError(null);
    setValidationErrors([]);
    // Send only the changed keys — the backend applies a partial
    // update; unchanged keys just widen the validation surface.
    const changed = diffTeamSettings(payload.settings, draft);
    try {
      await mutation.save(changed);
      setSavedAt(Date.now());
      toast.success("Team settings saved");
    } catch (err) {
      toast.error("Couldn't save team settings", {
        description: errorMessage(err),
      });
      const apiErr = err as ApiError;
      // `apiFetch` already unwrapped FastAPI's `{ detail: ... }`
      // envelope, so `apiErr.detail` IS the 422 body:
      // `{ code, errors: [{ key, code, detail }] }` for a
      // validation failure, or a plain string otherwise.
      const detail = apiErr?.detail as
        | { code?: string; errors?: TeamSettingValidationError[] }
        | string
        | undefined;
      if (
        detail &&
        typeof detail === "object" &&
        Array.isArray(detail.errors)
      ) {
        setValidationErrors(detail.errors);
      } else {
        setSaveError(
          typeof detail === "string"
            ? detail
            : "Could not save team settings.",
        );
      }
    }
  };

  // --- Render guards ---------------------------------------------------

  if (settingsQuery.isLoading) {
    return (
      <div className="flex flex-col gap-5" data-testid="settings-page">
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="settings-loading"
        >
          Loading team settings…
        </p>
      </div>
    );
  }

  if (settingsQuery.isError) {
    return (
      <div className="flex flex-col gap-5" data-testid="settings-page">
        <p
          className="text-destructive font-mono text-sm px-3 py-6"
          data-testid="settings-load-error"
        >
          Failed to load team settings:{" "}
          {settingsQuery.error instanceof Error
            ? settingsQuery.error.message
            : "unknown error"}
        </p>
      </div>
    );
  }

  if (!payload) {
    return (
      <div className="flex flex-col gap-5" data-testid="settings-page">
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="settings-no-team"
        >
          No team selected.
        </p>
      </div>
    );
  }

  const schema = payload.schema;

  /** Render one control for a setting key, dispatching on its
   * schema `kind`. Returns `null` if the schema doesn't carry the
   * key (forward-compat: a removed key just stops rendering). */
  const renderControl = (key: string) => {
    const entry = schema[key];
    if (!entry) return null;
    const labels = SETTING_LABELS[key] ?? { label: key, description: "" };
    const testid = `setting-${key.replace(/\./g, "-")}`;

    if (entry.kind === "bool") {
      return (
        <SettingToggle
          key={key}
          label={labels.label}
          description={labels.description}
          checked={Boolean(draft[key as keyof TeamSettings])}
          disabled={!canEdit}
          onChange={(next) => setKey(key as keyof TeamSettings, next)}
          data-testid={testid}
        />
      );
    }

    // `enum` / `array_enum` both render from `choices`.
    let choices = entry.choices ?? [];
    // Q1 divergence: hide `player` from the summary-visibility
    // picker — a `player`-visibility summary is invisible to
    // viewers (matches the M6a composer fix).
    if (key === "summaries.default_visibility") {
      choices = choices.filter((c) => !SUMMARY_VISIBILITY_OMIT.has(c));
    }
    const options = choices.map((value) => ({
      value,
      label: choiceLabel(value),
    }));

    if (entry.kind === "enum") {
      return (
        <SettingChipGroup
          key={key}
          label={labels.label}
          description={labels.description}
          options={options}
          value={String(draft[key as keyof TeamSettings] ?? "")}
          disabled={!canEdit}
          onChange={(next) => setKey(key as keyof TeamSettings, next)}
          data-testid={testid}
        />
      );
    }

    // array_enum
    const current = draft[key as keyof TeamSettings];
    return (
      <SettingChipMulti
        key={key}
        label={labels.label}
        description={labels.description}
        options={options}
        values={Array.isArray(current) ? (current as string[]) : []}
        disabled={!canEdit}
        onChange={(next) => setKey(key as keyof TeamSettings, next)}
        data-testid={testid}
      />
    );
  };

  return (
    <div className="flex flex-col gap-5" data-testid="settings-page">
      <PageHeader
        title="Team Settings"
        description={
          <>
            {payload.team.name} · AI drafting governance and coaching
            defaults for the active team.
          </>
        }
        actions={
          !canEdit ? (
            <span
              className="rounded px-2 py-1 text-xs font-display uppercase tracking-wider bg-surface text-ink-muted"
              data-testid="settings-readonly-pill"
            >
              Read only
            </span>
          ) : undefined
        }
      />

      {!canEdit ? (
        <p
          className="text-ink-muted text-sm"
          data-testid="settings-readonly-note"
        >
          You can review these settings, but only a team admin can change
          AI governance and coaching defaults.
        </p>
      ) : null}

      {/* Two-column section layout — the four setting cards used to
       * stack full-width in a single column, leaving a wide band of
       * dead space on desktop and forcing a long vertical scroll.
       *
       * Two explicit `flex flex-col` columns (not a CSS grid): a grid
       * aligns cells row-by-row, so a short card (Visibility — one
       * toggle) next to a tall one (AI Governance — four controls)
       * would leave a gap below it. Independent flex columns let each
       * card pack tightly under the one above it. Columns collapse to
       * a single stack below `lg`.
       *
       * Left: AI Governance (4 controls) + Coaching Defaults (3).
       * Right: Visibility (1 toggle) + Streaming (3 fields). */}
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
        <div className="flex flex-1 flex-col gap-5">
          {/* Section 1 — AI Governance. */}
          <section
            className="flex flex-col gap-4 rounded border border-rule bg-surface p-4 shadow-sm"
            aria-labelledby="settings-ai-heading"
            data-testid="settings-section-ai"
          >
            <h3
              id="settings-ai-heading"
              className="text-sm font-display uppercase tracking-wider text-ink-muted"
            >
              AI Governance
            </h3>
            {AI_KEYS.map(renderControl)}
          </section>

          {/* Section 2 — Coaching Defaults. */}
          <section
            className="flex flex-col gap-4 rounded border border-rule bg-surface p-4 shadow-sm"
            aria-labelledby="settings-defaults-heading"
            data-testid="settings-section-defaults"
          >
            <h3
              id="settings-defaults-heading"
              className="text-sm font-display uppercase tracking-wider text-ink-muted"
            >
              Coaching Defaults
            </h3>
            {DEFAULTS_KEYS.map(renderControl)}
          </section>
        </div>

        {/* Visibility + Streaming render for coaches too so the
         * settings surface has the same shape across coach/admin
         * roles. `canEdit` only decides whether write actions are
         * enabled. */}
        <div className="flex flex-1 flex-col gap-5">
          {/* Section 3 (M12a) — Public visibility. Writes are gated on
           * the same team_settings:manage capability as the rest of
           * the page. */}
          <VisibilitySection
            teamId={payload.team.id}
            teamName={payload.team.name}
            isPublic={!!payload.team.public}
            canEdit={canEdit}
          />

          {/* Section 4 (M12c) — Streaming. RTMP ingest URL + public
           * HLS URL + masked stream key + rotate action. */}
          <StreamingSection
            teamId={payload.team.id}
            teamSlug={payload.team.slug}
            teamName={payload.team.name}
            canEdit={canEdit}
          />

          {/* Section 5 (private-beta Phase 4) — Guest links. Expiring,
           * revocable viewing links for account-less family (grandparents).
           * Server gate is `match:write`, which every role that can open
           * Coach Settings holds — no separate canEdit wiring. */}
          <GuestLinksSection teamId={payload.team.id} />
        </div>
      </div>

      {/* Membership management lives in Admin > Access — that
       * surface is M8, so this is plain copy for now (no link). */}
      <p className="text-ink-muted text-xs" data-testid="settings-people-note">
        Manage team members and invites in Admin &gt; Access.
      </p>

      {/* Validation errors — one line per offending key. */}
      {validationErrors.length > 0 ? (
        <div
          className="text-destructive font-mono text-xs flex flex-col gap-0.5"
          data-testid="settings-validation-errors"
        >
          {validationErrors.map((e) => (
            <p key={e.key}>
              {e.key}: {e.detail}
            </p>
          ))}
        </div>
      ) : null}
      {saveError ? (
        <p
          className="text-destructive font-mono text-xs"
          data-testid="settings-save-error"
        >
          {saveError}
        </p>
      ) : null}
      {savedAt ? (
        <p
          className="text-ink-muted font-mono text-xs"
          data-testid="settings-saved-note"
        >
          Team settings saved.
        </p>
      ) : null}

      {canEdit ? (
        <div className="flex items-center gap-3">
          <Button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || mutation.isPending}
            data-testid="settings-save"
          >
            {mutation.isPending ? "Saving…" : "Save team settings"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// M12a — Public visibility toggle
// ---------------------------------------------------------------------------

/**
 * One-toggle Visibility section: flips ``Team.public``.
 *
 * The "make public" flow goes through `<ConfirmDialog>` because it
 * widens who can read this team's matches; the "make private" flow is
 * a direct PATCH (no confirm) — pulling back from public is a safe
 * retraction. Mirrors the M7.5 destructive-confirm pattern (write-the-
 * confirm-before-the-mutation).
 *
 * On success the active scope is invalidated (so the settings page +
 * any other team-scoped cache picks up the new flag) AND the public-
 * teams index cache is bumped so a coach navigating to `/teams`
 * immediately sees their team appear / disappear.
 */
function VisibilitySection({
  teamId,
  teamName,
  isPublic,
  canEdit,
}: {
  teamId: string;
  teamName: string;
  isPublic: boolean;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const flip = async (next: boolean) => {
    setError(null);
    setPending(true);
    try {
      await setTeamVisibility(teamId, next);
      // Re-fetch the active team's settings (which carries the team
      // row) AND nuke the public-teams index so /teams reflects the
      // new flag immediately.
      await invalidateScope();
      await queryClient.invalidateQueries({
        queryKey: unscopedKey("public-teams"),
      });
      setConfirmOpen(false);
    } catch (err) {
      setError(
        err instanceof ApiError && typeof err.detail === "string"
          ? err.detail
          : "Couldn't update team visibility.",
      );
    } finally {
      setPending(false);
    }
  };

  const onToggle = (next: boolean) => {
    if (!canEdit) return;
    if (next) {
      // Going public — confirm. Going private — no friction.
      setConfirmOpen(true);
    } else {
      void flip(false);
    }
  };

  return (
    <section
      className="flex flex-col gap-4 rounded border border-rule bg-surface p-4 shadow-sm"
      aria-labelledby="settings-visibility-heading"
      data-testid="settings-section-visibility"
    >
      <h3
        id="settings-visibility-heading"
        className="text-sm font-display uppercase tracking-wider text-ink-muted"
      >
        Visibility
      </h3>
      <SettingToggle
        label="Make team page public"
        description="When on, this team appears on the public /teams page and its matches are readable to anyone with the link."
        checked={isPublic}
        disabled={!canEdit || pending}
        onChange={onToggle}
        data-testid="setting-team-public"
      />
      {!canEdit ? (
        <p
          data-testid="settings-visibility-readonly"
          className="text-xs text-ink-muted"
        >
          Team admins control public team visibility.
        </p>
      ) : null}
      {error ? (
        <p
          data-testid="settings-visibility-error"
          className="text-destructive font-mono text-xs"
        >
          {error}
        </p>
      ) : null}
      {confirmOpen ? (
        <ConfirmDialog
          title="Make this team public?"
          message={
            <p>
              <strong>{teamName}</strong> will appear on the public
              /teams page. Anyone with the link will be able to see this
              team&rsquo;s matches and replays. You can turn this off again
              at any time.
            </p>
          }
          confirmLabel={pending ? "Updating…" : "Make public"}
          onConfirm={() => void flip(true)}
          onClose={() => {
            if (pending) return;
            setConfirmOpen(false);
            setError(null);
          }}
          data-testid="settings-visibility-confirm"
        />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// M12c — Streaming section
// ---------------------------------------------------------------------------

/**
 * Streaming section: RTMP ingest URL + public HLS URL + masked stream
 * key + Rotate action.
 *
 * Privacy invariant — the stream key surfaces ONLY ONCE per rotation
 * (matches the M8b/M9a dev-token pattern). Before the first rotation
 * the masked key shows "Not yet generated"; after rotation we hold the
 * raw key in component state only as long as the operator needs to
 * copy it. The "Rotate key" action goes through `<ConfirmDialog>` —
 * rotation is destructive (it invalidates the live RTMP URL).
 */
function StreamingSection({
  teamId,
  teamSlug,
  teamName,
  canEdit,
}: {
  teamId: string;
  teamSlug: string;
  teamName: string;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [rotated, setRotated] = useState<{
    key: string;
    streamPath: string;
  } | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [revealed, setRevealed] = useState(false);

  // Last-rotated audit row — refetched on focus so an admin who
  // rotated from /admin/live a moment ago shows up here when the
  // coach comes back to the tab. Refresh isn't aggressive (60s
  // stale) — the notice is informational, not load-bearing.
  const auditQuery = useQuery({
    queryKey: unscopedKey("team-stream-key-audit", { teamId }),
    queryFn: () => getTeamStreamKeyAudit(teamId),
    enabled: canEdit,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  // The RTMP URL is the global base (`live_rtmp_public_url` setting,
  // configured in Admin → Settings → Live). We intentionally do NOT
  // fall back to a guessed `rtmp://<host>:1935` URL: a copyable URL
  // that quietly resolves to localhost would mislead a team admin
  // running this in production. When the base isn't configured we
  // show an inline notice instead.
  //
  // The team slug + stream key are NOT appended to this URL — the
  // operator pastes them together into OBS's "Stream Key" field
  // (see the streamKeyField below). OBS concatenates Server + "/" +
  // Stream Key as the RTMP path, so putting the slug in Server too
  // would double it.
  const appSettingsQuery = useQuery({
    queryKey: publicAppSettingsKey(),
    queryFn: getAppSettings,
    staleTime: 60_000,
  });
  const rtmpBaseRaw = (
    appSettingsQuery.data?.settings?.live_rtmp_public_url ?? ""
  ).trim();
  const rtmpBase = rtmpBaseRaw.replace(/\/+$/, "");
  const rtmpUrl = rtmpBase;
  const hlsUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/api/live/hls/teams/${teamSlug}/index.m3u8`
      : `/api/live/hls/teams/${teamSlug}/index.m3u8`;
  // Composite Stream Key field — what the operator actually pastes
  // into OBS. Derived from the server's own `stream_path` (multi-club
  // canonical `live/<club>/<team>/<key>`, minus the `live/` prefix the
  // Server URL already carries) so the pasteable value always matches
  // what the auth webhook validates — never rebuilt client-side from
  // the slug. We hide the raw key behind the same Reveal/Hide toggle
  // the bare key used to use.
  const streamKeyField = rotated
    ? rotated.streamPath.replace(/^live\//, "")
    : null;

  const onRotate = async () => {
    setError(null);
    setPending(true);
    try {
      const resp = await rotateTeamStreamKey(teamId);
      setRotated({ key: resp.stream_key, streamPath: resp.stream_path });
      setRevealed(true);
      setConfirmOpen(false);
      // Update the audit query cache with the values the rotate
      // response just echoed back — saves a round-trip and avoids a
      // stale notice between rotate and the next focus refetch.
      queryClient.setQueryData(
        unscopedKey("team-stream-key-audit", { teamId }),
        {
          last_rotated_at: resp.last_rotated_at,
          last_rotated_by: resp.last_rotated_by,
        },
      );
    } catch (err) {
      setError(
        err instanceof ApiError && typeof err.detail === "string"
          ? err.detail
          : "Couldn't rotate the stream key.",
      );
    } finally {
      setPending(false);
    }
  };

  const copyToClipboard = (value: string) => {
    try {
      void navigator.clipboard?.writeText(value);
    } catch {
      // Older browsers / non-secure contexts: leave the value visible
      // so the operator can copy manually.
    }
  };

  return (
    <section
      className="flex flex-col gap-4 rounded border border-rule bg-surface p-4 shadow-sm"
      aria-labelledby="settings-streaming-heading"
      data-testid="settings-section-streaming"
    >
      <h3
        id="settings-streaming-heading"
        className="text-sm font-display uppercase tracking-wider text-ink-muted"
      >
        Streaming
      </h3>
      <p className="text-ink-muted text-xs">
        In OBS or your sideline camera app, paste the RTMP server URL into{" "}
        <strong>Server</strong> and the team Stream Key (slug + key) into{" "}
        <strong>Stream Key</strong> to broadcast {teamName}&rsquo;s matches
        live. The Stream Key is shown <em>once</em> on rotation — copy it
        immediately; if you lose it, rotate again.
      </p>

      {rtmpUrl ? (
        <StreamingField
          label="RTMP server URL"
          value={rtmpUrl}
          onCopy={() => copyToClipboard(rtmpUrl)}
          testid="streaming-rtmp-url"
        />
      ) : (
        <div
          data-testid="streaming-rtmp-url-unconfigured"
          className="flex flex-col gap-1.5"
        >
          <span className="font-display text-xs uppercase tracking-wide text-ink-muted">
            RTMP server URL
          </span>
          <p className="rounded border border-rule bg-surface px-3 py-2 font-mono text-[0.7rem] leading-snug text-ink-muted">
            RTMP base URL is not configured. Ask a super admin to set{" "}
            <strong>Live → RTMP public URL</strong> in Admin Settings (e.g.{" "}
            <code>rtmp://stream.example.com/live</code>).
          </p>
        </div>
      )}
      <StreamingField
        label="Public HLS URL"
        value={hlsUrl}
        onCopy={() => copyToClipboard(hlsUrl)}
        testid="streaming-hls-url"
      />

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2 text-xs text-ink-muted">
          <span className="font-display uppercase tracking-wide">
            Stream key (paste into OBS)
          </span>
          {streamKeyField ? (
            <button
              type="button"
              onClick={() => setRevealed((v) => !v)}
              data-testid="streaming-reveal-toggle"
              className="font-mono text-[0.65rem] text-ink-muted underline hover:text-ink"
            >
              {revealed ? "Hide" : "Reveal"}
            </button>
          ) : null}
        </div>
        <div className="flex items-stretch gap-2">
          <code
            data-testid="streaming-key-value"
            className="flex-1 truncate rounded border border-rule bg-surface px-3 py-2 font-mono text-xs text-ink"
          >
            {streamKeyField
              ? revealed
                ? streamKeyField
                : // Fixed-length mask — slug shown so the operator can
                  // verify the right team without revealing the key.
                  `${teamSlug}/${"•".repeat(24)}`
              : "Not yet generated — rotate to create a key."}
          </code>
          {streamKeyField ? (
            <button
              type="button"
              onClick={() => copyToClipboard(streamKeyField)}
              data-testid="streaming-copy-key"
              className="rounded border border-rule px-3 py-2 font-display text-xs uppercase tracking-wide text-ink-muted hover:text-ink"
            >
              Copy
            </button>
          ) : null}
        </div>
        <p className="text-[0.65rem] text-ink-muted">
          Format: <code>{teamSlug}/&lt;key&gt;</code>. Rotating
          invalidates any encoder still using the old key.
        </p>
        {auditQuery.data?.last_rotated_at ? (
          <p
            data-testid="streaming-last-rotated"
            className="text-[0.65rem] text-ink-muted"
          >
            Last rotated by{" "}
            <strong className="text-ink">
              {auditQuery.data.last_rotated_by ?? "unknown"}
            </strong>{" "}
            at <code>{auditQuery.data.last_rotated_at}</code>. Rotating
            again locks them out.
          </p>
        ) : null}
      </div>

      {error ? (
        <p
          data-testid="streaming-error"
          className="text-destructive font-mono text-xs"
        >
          {error}
        </p>
      ) : null}

      <div className="flex items-center justify-end gap-2 border-t border-rule pt-3">
        <button
          type="button"
          onClick={() => {
            if (!canEdit) return;
            setError(null);
            setConfirmOpen(true);
          }}
          disabled={!canEdit || pending}
          data-testid="streaming-rotate-key"
          className="rounded border border-destructive/40 bg-destructive/10 px-3 py-1.5 text-xs font-display uppercase tracking-wider text-destructive transition-colors hover:bg-destructive/20 disabled:border-rule disabled:bg-surface disabled:text-ink-muted"
        >
          Rotate key
        </button>
      </div>

      {confirmOpen ? (
        <ConfirmDialog
          title="Rotate stream key?"
          message={
            <p>
              Generating a new stream key for <strong>{teamName}</strong> will
              immediately reject any in-progress broadcast using the previous
              key. Anyone who has the old key will have to be re-issued the
              new one before they can publish again. This cannot be undone.
            </p>
          }
          confirmLabel={pending ? "Rotating…" : "Rotate key"}
          danger
          onConfirm={() => void onRotate()}
          onClose={() => {
            if (pending) return;
            setConfirmOpen(false);
            setError(null);
          }}
          data-testid="streaming-rotate-confirm"
        />
      ) : null}
    </section>
  );
}

function StreamingField({
  label,
  value,
  onCopy,
  testid,
}: {
  label: string;
  value: string;
  onCopy: () => void;
  testid: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-display uppercase tracking-wide text-ink-muted">
        {label}
      </span>
      <div className="flex items-stretch gap-2">
        <code
          data-testid={`${testid}-value`}
          className="flex-1 truncate rounded border border-rule bg-surface px-3 py-2 font-mono text-xs text-ink"
        >
          {value}
        </code>
        <button
          type="button"
          onClick={onCopy}
          data-testid={`${testid}-copy`}
          className="rounded border border-rule px-3 py-2 font-display text-xs uppercase tracking-wide text-ink-muted hover:text-ink"
        >
          Copy
        </button>
      </div>
    </div>
  );
}


const GUEST_TTL_PRESETS = [
  { value: "12", label: "Match day (12 hours)" },
  { value: "24", label: "24 hours" },
  { value: "168", label: "7 days" },
];

function GuestLinksSection({ teamId }: { teamId: string }) {
  const queryClient = useQueryClient();
  const [ttl, setTtl] = useState("24");
  const [created, setCreated] = useState<CreatedShareLink | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const linksQuery = useQuery({
    queryKey: unscopedKey("team-share-links", { teamId }),
    queryFn: () => listTeamShareLinks(teamId),
    staleTime: 30_000,
  });
  const links: ShareLink[] = linksQuery.data?.links ?? [];

  // Token in the FRAGMENT, never a path segment: fragments are not sent
  // to servers, so the link can't be harvested from an access log (this
  // token is reusable for the life of the link, unlike an invite token).
  const guestUrl = (token: string) =>
    typeof window !== "undefined"
      ? `${window.location.origin}/guest#${token}`
      : `/guest#${token}`;

  const onCreate = async () => {
    setError(null);
    setPending(true);
    try {
      const resp = await createTeamShareLink(teamId, Number(ttl));
      setCreated(resp);
      await queryClient.invalidateQueries({
        queryKey: unscopedKey("team-share-links", { teamId }),
      });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  };

  const onRevoke = async (linkId: string) => {
    setError(null);
    try {
      await revokeTeamShareLink(teamId, linkId);
      if (created?.id === linkId) setCreated(null);
      await queryClient.invalidateQueries({
        queryKey: unscopedKey("team-share-links", { teamId }),
      });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const copy = (value: string) => {
    try {
      void navigator.clipboard?.writeText(value);
    } catch {
      // Non-secure context: the value stays visible for manual copy.
    }
  };

  const fmtExpiry = (epochSeconds: number) =>
    new Date(epochSeconds * 1000).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });

  return (
    <section
      className="flex flex-col gap-4 rounded border border-rule bg-surface p-4 shadow-sm"
      aria-labelledby="settings-guest-links-heading"
      data-testid="settings-section-guest-links"
    >
      <h3
        id="settings-guest-links-heading"
        className="text-sm font-display uppercase tracking-wider text-ink-muted"
      >
        Guest links
      </h3>
      <p className="text-ink-muted text-xs">
        Share an expiring link so family without an account (grandparents,
        relatives abroad) can watch this team&rsquo;s live streams and match
        video. Anyone with the link can watch until it expires or you revoke
        it — treat it like a key, and revoke it if it spreads further than
        intended. The link is shown <em>once</em>; copy it right away.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <TkticSelect
          value={ttl}
          onChange={setTtl}
          options={GUEST_TTL_PRESETS}
          ariaLabel="Guest link duration"
          data-testid="guest-link-ttl"
          className="w-48"
        />
        <Button
          type="button"
          size="sm"
          onClick={() => void onCreate()}
          disabled={pending}
          data-testid="guest-link-create"
        >
          {pending ? "Creating…" : "Create guest link"}
        </Button>
      </div>

      {created ? (
        <div className="flex flex-col gap-1.5" data-testid="guest-link-created">
          <span className="font-display text-xs uppercase tracking-wide text-ink-muted">
            New link (shown once)
          </span>
          <div className="flex items-stretch gap-2">
            <code
              data-testid="guest-link-created-url"
              className="flex-1 truncate rounded border border-rule bg-surface px-3 py-2 font-mono text-xs text-ink"
            >
              {guestUrl(created.token)}
            </code>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => copy(guestUrl(created.token))}
              data-testid="guest-link-created-copy"
            >
              Copy
            </Button>
          </div>
          <span className="font-mono text-[0.65rem] text-ink-muted">
            Works until {fmtExpiry(created.expires_at)}.
          </span>
        </div>
      ) : null}

      {error ? (
        <p
          data-testid="guest-link-error"
          className="text-destructive text-xs font-mono"
        >
          {error}
        </p>
      ) : null}

      {links.length > 0 ? (
        <ul className="flex flex-col gap-1.5" data-testid="guest-link-list">
          {links.map((link) => (
            <li
              key={link.id}
              data-testid="guest-link-row"
              data-active={link.active ? "true" : "false"}
              className="flex items-center justify-between gap-2 rounded border border-rule bg-canvas px-3 py-2"
            >
              <span className="font-mono text-[0.7rem] text-ink-muted">
                {link.revoked_at
                  ? "Revoked"
                  : link.active
                    ? `Active — expires ${fmtExpiry(link.expires_at)}`
                    : `Expired ${fmtExpiry(link.expires_at)}`}
                {" · created "}
                {new Date(link.created_at).toLocaleDateString()}
              </span>
              {link.active ? (
                <button
                  type="button"
                  onClick={() => void onRevoke(link.id)}
                  data-testid="guest-link-revoke"
                  className="rounded border border-rule px-2 py-1 font-display text-[0.65rem] uppercase tracking-wider text-ink-muted transition-colors hover:bg-field hover:text-ink"
                >
                  Revoke
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
